import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsLeftRight, CheckCircle, UserCircle, X } from "@phosphor-icons/react";
import { SeamPreview } from "@/components/SeamPreview";
import { api, formatApiErrorDetail } from "@/lib/api";

// Staff view of the arrangement a customer made in the portal, with one-click apply.
export const CustomerVeinProposal = ({ quote, onApplied }) => {
  const [prop, setProp] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api
      .get(`/quotes/${quote.id}/vein-proposal`)
      .then(({ data }) => setProp(data.proposal))
      .catch(() => setProp(null));
  }, [quote.id, quote.updated_at]);

  if (!prop || prop.status !== "proposed") return null;
  const installRot = quote.layout?.install_rot || {};
  const pieces = (quote.areas || [])
    .flatMap((a) => (a.pieces || []).filter((p) => (p.points || []).length > 2 && !p.panel_kind))
    .map((p) => {
      const ir = installRot[p.id] || 0;
      return ir ? { ...p, rot: ((((Number(p.rot) || 0) + ir) % 360) + 360) % 360 } : p;
    });
  const bookmatched = (prop.slabs || []).filter((s) => s.twin_of).length;

  const act = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/vein-proposal/${kind}`);
      onApplied?.(data);
      setProp((p) => ({ ...p, status: kind === "apply" ? "applied" : "dismissed" }));
      toast.success(kind === "apply" ? "Customer layout applied to the slab layout" : "Proposal dismissed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the proposal");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-cyan-500/40 bg-cyan-500/[0.06] p-4" data-testid="customer-vein-proposal">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-cyan-100">
            <UserCircle size={18} weight="bold" /> Customer arranged their pieces on the slab
          </div>
          <p className="mt-1 text-xs text-zinc-400">
            {prop.by || "The customer"} placed {Object.keys(prop.placements || {}).length} piece
            {Object.keys(prop.placements || {}).length === 1 ? "" : "s"} across {(prop.slabs || []).length} slab
            {(prop.slabs || []).length === 1 ? "" : "s"}
            {bookmatched ? ` · ${bookmatched} book-matched twin${bookmatched === 1 ? "" : "s"}` : ""}
            {prop.updated_at ? ` · ${new Date(prop.updated_at).toLocaleString()}` : ""}
          </p>
          {prop.note && (
            <p className="mt-2 rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs text-zinc-200" data-testid="customer-vein-note">
              “{prop.note}”
            </p>
          )}
          <div className="mt-3 max-w-xl rounded-lg border border-zinc-800 bg-zinc-950 p-2" data-testid="customer-vein-preview">
            <SeamPreview pieces={pieces} slabs={prop.slabs || []} placements={prop.placements || {}} labels tight seams />
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2">
          <button
            type="button"
            data-testid="customer-vein-apply"
            disabled={!!busy}
            onClick={() => act("apply")}
            className="flex h-10 items-center gap-2 rounded-full bg-cyan-500 px-5 text-xs font-bold uppercase tracking-widest text-zinc-950 transition-colors duration-150 hover:bg-cyan-400 disabled:opacity-50"
          >
            <CheckCircle size={15} weight="bold" /> {busy === "apply" ? "Applying…" : "Apply to layout"}
          </button>
          <button
            type="button"
            data-testid="customer-vein-dismiss"
            disabled={!!busy}
            onClick={() => act("dismiss")}
            className="flex h-10 items-center gap-2 rounded-full border border-zinc-700 px-5 text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 disabled:opacity-50"
          >
            <X size={14} weight="bold" /> Dismiss
          </button>
          {bookmatched > 0 && (
            <span className="flex items-center gap-1.5 text-[10px] text-zinc-500">
              <ArrowsLeftRight size={12} /> Applying adds the mirrored twin slabs
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerVeinProposal;
